"use client";

import { useRef, useEffect } from "react";

interface HeroVideoProps {
  src: string;
  poster: string;
  className?: string;
}

export default function HeroVideo({ src, poster, className }: HeroVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const userPref = document.documentElement.getAttribute("data-reduce-motion") === "true";

    if (mediaQuery.matches || userPref) return;

    const video = videoRef.current;
    if (!video) return;

    const loadAndPlay = () => {
      if (hasLoadedRef.current) return;
      hasLoadedRef.current = true;
      video.src = src;
      video.load();
      video.play().catch(() => {});
    };

    if (!("IntersectionObserver" in window)) {
      loadAndPlay();
      return;
    }

    const observer = new IntersectionObserver(
      ([_entry]) => {
        if (_entry?.isIntersecting) {
          loadAndPlay();
          observer.unobserve(_entry.target);
        }
      },
      { threshold: 0.1, rootMargin: "100px" }
    );

    observer.observe(video);

    const handleMotionChange = () => {
      const userPrefNow = document.documentElement.getAttribute("data-reduce-motion") === "true";
      if (mediaQuery.matches || userPrefNow) {
        observer.disconnect();
      }
    };

    mediaQuery.addEventListener("change", handleMotionChange);

    const mutationObserver = new MutationObserver(handleMotionChange);
    mutationObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-reduce-motion"],
    });

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener("change", handleMotionChange);
      mutationObserver.disconnect();
    };
  }, [src]);

  return (
    <video
      ref={videoRef}
      muted
      loop
      playsInline
      preload="none"
      disablePictureInPicture
      poster={poster}
      aria-label="Flamingos flying in the sky"
      className={className}
    />
  );
}
